// WhatsappChatStarted.jsx

import React, { Component } from "react";
import ChatInput from "../ChatInput";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../../withRouters/withRoutersHook";
import { connect } from "react-redux";
import { FetchIndividualWhatsAppMessagesAction } from "../../../../Actions/Messaging";
import PlaceholderImage1 from "../../../../Assets/svg/PlaceholderImage1.svg";
import DotsThreeVerticalSVG from "../../../../Assets/svg/DotsThreeVertical.svg";
import SaveContact from "./SaveContact";
import Options from "../WhatsApp/Options";

class WhatsappChatStarted extends Component {
  static propTypes = {
    // Redux
    FetchIndividualWhatsAppMessagesAction: PropTypes.func.isRequired,
    individualChatSession: PropTypes.array,
    individualChatSessionFetchSuccess: PropTypes.bool,

    // Router
    navigate: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,

    showChatAssistant: PropTypes.func,
    isChatAssistantVisible: PropTypes.bool,
    seekRange: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  };

  constructor(props) {
    super(props);
    this.messageRefs = [];
    this.chatEndRef = React.createRef();
    this.scrollContainerRef = React.createRef();

    this.state = {
      showOptions: null,
      showSaveContant: false,
      currentSessionId: null,
      isSessionLoading: false,
      scrollProgress: 0,
      scrollThumbHeight: 30,
    };
  }

  componentDidMount() {
    const sessionId = this.getSessionIdFromPath();
    this.loadSession(sessionId);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.individualChatSession !== this.props.individualChatSession) {
      this.scrollToBottom();
      setTimeout(this.updateScrollThumb, 50);
    }

    const newSessionId = this.getSessionIdFromPath();
    if (newSessionId && newSessionId !== this.state.currentSessionId) {
      this.closeWebSocket();
      this.loadSession(newSessionId);
    }

    if (prevProps.seekRange !== this.props.seekRange) {
      this.handleSeek(this.props.seekRange);
    }
  }

  componentWillUnmount() {
    this.closeWebSocket();
  }

  getSessionIdFromPath = () => {
    const { pathname } = this.props.location;
    // This returns what's after the last "/" in the URL
    return pathname.split("/").pop().replace(/\+/g, "");
  };

  loadSession = (sessionId) => {
    if (!sessionId) return;

    this.setState(
      { currentSessionId: sessionId, isSessionLoading: true },
      async () => {
        try {
          await this.props.FetchIndividualWhatsAppMessagesAction(sessionId);
          this.openWebSocket(sessionId);
        } catch (error) {
          console.error("Error loading WhatsApp session:", error);
        } finally {
          if (this.state.currentSessionId === sessionId) {
            this.setState({ isSessionLoading: false });
          }
        }
      }
    );
  };

  openWebSocket = (sessionId) => {
    if (!sessionId) return;

    const wsUrl = `wss://${window.location.host}/ws/chat/${sessionId}/`;
    this.chatSocket = new WebSocket(wsUrl);

    this.chatSocket.onopen = () => {
      console.log("[WebSocket] Connected:", wsUrl);
    };

    this.chatSocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("New message:", data);
      this.props.FetchIndividualWhatsAppMessagesAction(sessionId);
    };

    this.chatSocket.onerror = (error) => {
      console.error("[WebSocket] Error:", error);
    };

    this.chatSocket.onclose = (event) => {
      console.log("[WebSocket] Closed:", event.code, event.reason);
    };
  };

  closeWebSocket = () => {
    if (this.chatSocket) {
      this.chatSocket.close();
      this.chatSocket = null;
    }
  };

  scrollToBottom = () => {
    if (this.chatEndRef.current) {
      this.chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  formatDate = (date) => {
    return new Date(date).toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });
  };

  formatMessageDate = (date) => {
    const messageTime = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (messageTime >= today) {
      return "Today";
    } else if (messageTime >= yesterday) {
      return "Yesterday";
    } else {
      return messageTime.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  handleSeek = (range) => {
    if (!range) return;
    const start = new Date(range.start);
    const end = new Date(range.end);
    const { individualChatSession } = this.props;

    if (!individualChatSession || individualChatSession.length === 0) return;

    const indexToScroll = individualChatSession.findIndex(
      (msg) => new Date(msg.created_at) >= start
    );

    if (indexToScroll !== -1 && this.messageRefs[indexToScroll]) {
      this.messageRefs[indexToScroll].scrollIntoView({ behavior: "smooth" });
    } else {
      console.warn("No messages found after that start timestamp");
    }
  };

  updateScrollThumb = () => {
    const el = this.scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const scrollable = scrollHeight - clientHeight;
    const progress = scrollable > 0 ? (scrollTop / scrollable) * 100 : 0;
    const thumbHeight = Math.max(10, (clientHeight / scrollHeight) * 100);
    this.setState({ scrollProgress: progress, scrollThumbHeight: thumbHeight });
  };

  handleScroll = () => {
    this.updateScrollThumb();
  };

  handleShowOptions = () => {
    this.setState((prevState) => ({
      showOptions: !prevState.showOptions,
    }));
  };

  handleCloseOptions = () => {
    this.setState({ showOptions: false });
  };

  handleShowSaveContact = () => {
    this.setState((prevState) => ({
      showSaveContant: !prevState.showSaveContant,
    }));
  };

  renderMessages = () => {
    const { individualChatSession, individualChatSessionFetchSuccess } = this.props;
    const { isSessionLoading } = this.state;

    if (isSessionLoading || (!individualChatSessionFetchSuccess && !individualChatSession)) {
      return (
        <div className="flex min-h-full flex-col justify-center gap-5 px-4 py-6">
          <div className="self-center rounded-full border border-blue-100 bg-white px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-blue-500 shadow-sm">
            Loading conversation
          </div>
          <div className="animate-pulse space-y-4">
            <div className="flex justify-start">
              <div className="w-[42%] rounded-2xl rounded-tl-md bg-gray-200/80 px-4 py-3 shadow-sm">
                <div className="h-3 w-24 rounded-full bg-gray-300/80" />
                <div className="mt-2 h-3 w-full rounded-full bg-gray-300/70" />
                <div className="mt-2 h-3 w-3/4 rounded-full bg-gray-300/60" />
              </div>
            </div>
            <div className="flex justify-end">
              <div className="w-[38%] rounded-2xl rounded-tr-md bg-blue-100/90 px-4 py-3 shadow-sm">
                <div className="h-3 w-20 rounded-full bg-blue-200/90" />
                <div className="mt-2 h-3 w-full rounded-full bg-blue-200/80" />
                <div className="mt-2 h-3 w-2/3 rounded-full bg-blue-200/70" />
              </div>
            </div>
            <div className="flex justify-start">
              <div className="w-[50%] rounded-2xl rounded-tl-md bg-gray-200/80 px-4 py-3 shadow-sm">
                <div className="h-3 w-16 rounded-full bg-gray-300/80" />
                <div className="mt-2 h-3 w-full rounded-full bg-gray-300/70" />
                <div className="mt-2 h-3 w-5/6 rounded-full bg-gray-300/60" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!individualChatSession || individualChatSession.length === 0) {
      return (
        <div className="flex flex-1 min-h-full items-center justify-center">
          <div className="text-center font-inter">
            <p className="text-lg font-semibold text-gray-700">No messages yet</p>
            <p className="mt-1 text-sm text-gray-400">
              This conversation is empty.
            </p>
          </div>
        </div>
      );
    }

    let lastMessageDate = null;

    return individualChatSession.map((message, index) => {
      const messageDate = new Date(message.created_at).toDateString();
      const showDateLabel = messageDate !== lastMessageDate;
      lastMessageDate = messageDate;

      return (
        <React.Fragment key={index}>
          {showDateLabel && (
            <div className="text-center font-inter font-[500] text-[0.875rem] text-gray-500 my-4">
              {this.formatMessageDate(message.created_at)}
            </div>
          )}
          <div
            ref={(el) => (this.messageRefs[index] = el)}
            className={`flex ${
              message.message_type === "incoming" ? "" : "justify-end"
            }`}
          >
            <div
              className={`relative flex max-w-[70%] p-3 rounded-lg shadow ${
                message.message_type === "incoming"
                  ? "bg-gray-200 text-gray-900 w-[40%] pt-3 pb-6"
                  : "bg-primary-blue text-white w-[40%] pt-3 pb-6"
              }`}
            >
              <div className="leading-relaxed">{message.body}</div>
              <div className="absolute right-[1rem] bottom-[0.5rem] text-[0.7rem]">
                {this.formatDate(message.created_at)}
              </div>
              <div
                className={`absolute top-2 ${
                  message.message_type === "incoming"
                    ? "left-[-8px]"
                    : "right-[-8px]"
                } h-4 w-4 rotate-45 ${
                  message.message_type === "incoming"
                    ? "bg-gray-200"
                    : "bg-primary-blue"
                }`}
              ></div>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  render() {
    // Retrieve the userName directly from route state:
    const userNameFromLocation = this.props.location?.state?.userName;
    const hasSavedContactName =
      !!userNameFromLocation && userNameFromLocation !== this.state.currentSessionId;
    const {
      showChatAssistant,
      isChatAssistantVisible,
      individualChatSession,
      individualChatSessionFetchSuccess,
    } = this.props;
    const { isSessionLoading } = this.state;
    return (
      <div className="relative w-full h-full border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        {this.state.showSaveContant && (
          <SaveContact
            handleShowSaveContact={this.handleShowSaveContact}
            initialName={hasSavedContactName ? userNameFromLocation : ""}
            isRename={hasSavedContactName}
          />
        )}

        {/* Header */}
        <div className="w-full h-14 bg-white border-b border-blue-100 px-4 flex justify-between items-center rounded-t-xl shrink-0">
          <div className="flex items-center gap-3">
            <img
              src={PlaceholderImage1}
              alt="Chat Icon"
              className="h-9 w-9 rounded-full bg-primary-blue/10"
            />
            <div>
              <div className="font-SFPro font-semibold text-base leading-tight text-gray-900">
                {userNameFromLocation || this.state.currentSessionId}
              </div>
              <div className="font-inter text-xs text-gray-500">
                Tap profile for more info
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isChatAssistantVisible && (
              <button
                onClick={showChatAssistant}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary-blue to-blue-400 text-white text-sm font-medium hover:from-blue-600 hover:to-blue-500 transition-all duration-150 font-inter shadow-sm"
              >
                Chat Assistant
              </button>
            )}
            <div className="relative">
              <button
                onClick={this.handleShowOptions}
                className="relative p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <img src={DotsThreeVerticalSVG} alt="Options" className="w-5 h-5" />
              </button>
              {this.state.showOptions && (
                <Options
                  handleShowSaveContact={this.handleShowSaveContact}
                  handleCloseOptions={this.handleCloseOptions}
                  isRename={hasSavedContactName}
                />
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="relative flex-1 overflow-hidden">
          {/* Custom scroll progress indicator */}
          <div className="absolute right-1.5 top-3 bottom-3 w-[5px] bg-gray-100 rounded-full z-10 pointer-events-none">
            <div
              className="absolute w-full bg-primary-blue rounded-full transition-[top] duration-150 ease-out"
              style={{
                height: `${this.state.scrollThumbHeight}%`,
                top: `${this.state.scrollProgress * (1 - this.state.scrollThumbHeight / 100)}%`,
              }}
            />
          </div>

          <div
            ref={this.scrollContainerRef}
            onScroll={this.handleScroll}
            className="no-scrollbar flex flex-col gap-[1rem] px-4 pr-6 py-3 overflow-y-scroll h-full"
            style={{ scrollbarWidth: "none" }}
          >
            {this.renderMessages()}

            <div ref={this.chatEndRef} />
          </div>
        </div>

        {/* Input — pinned to bottom by flex column */}
        <div className="shrink-0">
          <ChatInput />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  individualChatSession: state.messaging.individualChatSession,
  individualChatSessionFetchSuccess:
    state.messaging.individualChatSessionFetchSuccess,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchIndividualWhatsAppMessagesAction,
  })(WhatsappChatStarted)
);
